import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Smartphone,
  MessageSquare,
  Layers,
  Zap,
  BellRing,
  LayoutDashboard,
  Package,
  TrendingUp,
  Map,
  Megaphone,
  Building2,
  Check,
} from "lucide-react";
import PageShell from "@/components/opsiq/PageShell";
import Seo from "@/components/Seo";

const PHASE_1 = [
  { Icon: Smartphone,     h: "Pickup agent app",        p: "Count the clothes with the customer, confirm the order, take payment — all on one screen." },
  { Icon: MessageSquare,  h: "Automatic receipts",      p: "Instant WhatsApp & email receipt with every item listed and a delivery estimate." },
  { Icon: Layers,         h: "Smart order queue",       p: "Orders queued by colour and category before the clothes even arrive at your shop." },
  { Icon: Zap,            h: "Arrival & wash start",    p: "Staff mark when clothes arrive and when washing starts — one tap, no paperwork." },
  { Icon: BellRing,       h: "Real-time notifications", p: "Customers and you get notified at every stage: received, washing, ready, delivered." },
  { Icon: LayoutDashboard,h: "Live owner dashboard",    p: "Today's orders, revenue, and pending work on your phone — anywhere in Ghana." },
];

const PHASE_2 = [
  { Icon: Package,    h: "Stock & detergent tracking",   p: "Know exactly how much you use per order and get alerts before you run out." },
  { Icon: TrendingUp, h: "Popular services analytics",   p: "See which services make the most money and push what works." },
  { Icon: Map,        h: "Customer location mapping",    p: "A heatmap of where your customers live, so you know where to send pickup agents next." },
  { Icon: Megaphone,  h: "Promotional insights",         p: "Find quiet days and slow hours, then send targeted offers to fill them." },
  { Icon: Building2,  h: "Branch expansion intelligence",p: "Data-backed answers to \u201Cwhere should my next branch be?\u201D — with an expansion readiness score." },
];

const PLANS = [
  {
    name: "Basic",
    pitch: "Get your operations digital",
    featured: false,
    items: ["All Phase 1 operations features", "Pickup agent app & live dashboard", "Automatic WhatsApp & email receipts", "Monthly message bundle included", "Daily, weekly & monthly reports"],
  },
  {
    name: "Pro",
    pitch: "Operations + business intelligence",
    featured: true,
    items: ["Everything in Basic", "All Phase 2 intelligence features", "Stock & detergent tracking", "Customer location heatmap", "Deeper reports & analytics"],
  },
  {
    name: "Premium",
    pitch: "The full suite for growing brands",
    featured: false,
    items: ["Everything in Pro", "Multi-branch support", "Custom branding (your logo & colours)", "Priority support", "CEO-level BI reports"],
  },
];

export default function WashflowProductPage() {
  return (
    <>
      <Seo
        title="WashFlow — Laundry Operations & Intelligence"
        description="WashFlow, powered by OpsIQ. A laundry operations and intelligence platform for Ghanaian businesses: track every garment, message every customer on WhatsApp, grow with data. Basic, Pro, Premium plans."
        path="/products/washflow"
        image="/products/og-washflow.jpg"
        imageAlt="WashFlow — Run your laundry smarter, from pickup to delivery."
      />
      <PageShell
      testid="washflow-page"
      eyebrow="WashFlow · Laundry operations"
      title={
        <>
          Run your laundry smarter, <span className="italic">from pickup to delivery.</span>
        </>
      }
      subtitle="A laundry operations and intelligence platform for Ghanaian businesses. Track every garment, message every customer on WhatsApp, and see exactly how your business is doing — from your phone."
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/products"
            data-testid="washflow-back"
            className="inline-flex items-center gap-2 text-[11px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/55 hover:text-[#D9FF3C] transition-colors"
          >
            <ArrowLeft size={14} /> Back to Products
          </Link>
          <div className="h-4 w-px bg-white/15" />
          <a
            href="https://washflow.opsiq.live/?utm_source=opsiq-main"
            target="_blank"
            rel="noreferrer"
            data-testid="washflow-full-site"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#D9FF3C] text-[#142645] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E4FF66] transition-colors"
          >
            Visit full WashFlow site <ArrowUpRight size={14} />
          </a>
          <a
            href="https://wa.me/233546080065?text=Hello%20OpsIQ%20%E2%80%94%20I%27d%20like%20a%20WashFlow%20demo%20for%20my%20laundry."
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 border border-[#F1ECE0]/25 text-[#F1ECE0] text-[11px] uppercase tracking-[0.22em] font-semibold hover:border-[#D9FF3C] hover:text-[#D9FF3C] transition-colors"
          >
            WhatsApp us
          </a>
        </div>
      </div>

      {/* The problem */}
      <section className="bg-[#F1ECE0] text-[#1A2E4A] border-t border-[#1A2E4A]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-plex-mono uppercase tracking-[0.26em] text-[#1A2E4A] mb-5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#B8E024] mr-2" />
              The problem
            </div>
            <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              Running a laundry on paper and memory is <span className="italic">costing you money.</span>
            </h2>
            <p className="mt-5 text-[#1A2E4A]/75 max-w-md">
              Most laundries in Ghana run on notebooks, WhatsApp chats and guesswork. Five expensive problems follow.
            </p>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              ["Lost or mixed-up clothes", "Garments go missing or end up with the wrong customer — and you pay for it."],
              ["Customer disputes", "“I gave you 12 shirts, not 10!” Arguments with no proof on either side."],
              ["No visibility", "You can't see what's happening in your shop unless you're standing there."],
              ["Staff errors", "Handwritten tickets get lost, misread, or filled in wrong at busy times."],
              ["No data", "Which service makes the most money? Which customers are leaving? Nobody knows."],
            ].map(([h, p]) => (
              <li key={h} className="border border-[#1A2E4A]/15 p-5 bg-white">
                <h3 className="font-editorial text-xl leading-tight mb-2">{h}</h3>
                <p className="text-sm text-[#1A2E4A]/70">{p}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Phase 1 */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="text-[11px] font-plex-mono uppercase tracking-[0.26em] text-[#D9FF3C] mb-5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D9FF3C] mr-2" />
            Phase 1 · Operations
          </div>
          <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-[#F1ECE0]">
            Every garment tracked. <span className="italic">Every customer informed.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {PHASE_1.map(({ Icon, h, p }, i) => (
            <motion.div
              key={h}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="border border-white/10 bg-[#142645]/60 p-6 md:p-7"
            >
              <Icon size={20} strokeWidth={1.5} className="text-[#D9FF3C] mb-5" />
              <h3 className="font-editorial text-xl text-[#F1ECE0] leading-tight mb-2">{h}</h3>
              <p className="text-sm text-[#F1ECE0]/65 leading-relaxed">{p}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Phase 2 */}
      <section className="bg-[#F1ECE0] text-[#1A2E4A] border-t border-[#1A2E4A]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl mb-10 md:mb-14">
            <div className="text-[11px] font-plex-mono uppercase tracking-[0.26em] text-[#1A2E4A] mb-5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#B8E024] mr-2" />
              Phase 2 · Intelligence & Growth
            </div>
            <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              Your data starts <span className="italic">working for you.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {PHASE_2.map(({ Icon, h, p }, i) => (
              <motion.div
                key={h}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="border border-[#1A2E4A]/15 bg-white p-6 md:p-7"
              >
                <Icon size={20} strokeWidth={1.5} className="text-[#1A2E4A] mb-5" />
                <h3 className="font-editorial text-xl leading-tight mb-2">{h}</h3>
                <p className="text-sm text-[#1A2E4A]/70 leading-relaxed">{p}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10 md:mb-14">
          <div>
            <div className="text-[11px] font-plex-mono uppercase tracking-[0.26em] text-[#D9FF3C] mb-5">
              Three plans
            </div>
            <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-[#F1ECE0] max-w-3xl">
              Sized to your business. <span className="italic">No hidden charges.</span>
            </h2>
          </div>
          <a
            href="https://washflow.opsiq.live/#pricing"
            target="_blank"
            rel="noreferrer"
            className="text-[11px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] hover:underline"
          >
            Full pricing on washflow.opsiq.live →
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={
                "border p-7 md:p-8 flex flex-col " +
                (plan.featured
                  ? "bg-[#D9FF3C] text-[#142645] border-[#D9FF3C]"
                  : "bg-[#142645]/60 text-[#F1ECE0] border-white/10")
              }
            >
              <div className="flex items-center justify-between mb-4">
                <div className="font-editorial text-3xl leading-tight">{plan.name}</div>
                {plan.featured && (
                  <span className="text-[10px] font-plex-mono uppercase tracking-[0.22em]">Most popular</span>
                )}
              </div>
              <p className={"text-sm mb-6 " + (plan.featured ? "text-[#142645]/80" : "text-[#F1ECE0]/70")}>
                {plan.pitch}
              </p>
              <ul className="space-y-3 flex-1">
                {plan.items.map((it) => (
                  <li key={it} className="flex gap-2.5 text-sm leading-relaxed">
                    <Check size={16} className="mt-0.5 flex-none" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://washflow.opsiq.live/#contact"
                target="_blank"
                rel="noreferrer"
                className={
                  "mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.22em] font-semibold transition-colors " +
                  (plan.featured
                    ? "bg-[#142645] text-[#D9FF3C] hover:bg-[#0E1830]"
                    : "border border-[#D9FF3C]/50 text-[#D9FF3C] hover:bg-[#D9FF3C] hover:text-[#142645]")
                }
              >
                Request {plan.name} quote <ArrowUpRight size={14} />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F1ECE0] text-[#1A2E4A] border-t border-[#1A2E4A]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11px] font-plex-mono uppercase tracking-[0.26em] text-[#1A2E4A] mb-5">
              Get started
            </div>
            <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Free demo with your <span className="italic">own services and prices.</span>
            </h2>
            <p className="mt-5 max-w-xl text-[#1A2E4A]/70">
              Tell us about your laundry business and we'll set up a free demo configured for your shop — no commitment.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a
              href="https://washflow.opsiq.live/#contact"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#1A2E4A] text-[#F1ECE0] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#0E1830] transition-colors"
            >
              Request my free demo <ArrowUpRight size={16} />
            </a>
            <a
              href="tel:+233546080065"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#1A2E4A]/30 text-[#1A2E4A] text-xs uppercase tracking-[0.22em] font-semibold hover:border-[#1A2E4A] transition-colors"
            >
              Call +233 54 608 0065
            </a>
          </div>
        </div>
      </section>
    </PageShell>
    </>
  );
}
